// Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth, API_BASE } from './state/auth.jsx';
import { useNavigate, Outlet, NavLink, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { Calendar, MessageSquare, Users, Home, ChevronDown, FileText, Settings, User, Building2, ChevronRight, LayoutDashboard, LogOut, Menu, X } from 'lucide-react';
import DateRangeFilter from './components/DateRangeFilter';
const profileDefaultImage = '/profileImage.png';

const Dashboard = () => {
  const [theme, setTheme] = useState('dark');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('Last 7 days');

  const isLight = theme === 'light';
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, switchProperty, api, user, tabActiveHotelId, setSelectedHotelId } = useAuth();
  const [properties, setProperties] = useState([]);
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const propertyDropdownListRef = React.useRef(null);
  const selectedPropertyItemRef = React.useRef(null);

  // Check if we are in settings section
  const isSettingsMode = location.pathname.includes('/settings');

  // Refs for click outside detection
  const dateDropdownRef = React.useRef(null);
  const propertyDropdownRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setDateDropdownOpen(false);
      }
      if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(event.target)) {
        setPropertyDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Scroll selected property into view when dropdown opens
  useEffect(() => {
    if (propertyDropdownOpen && selectedPropertyItemRef.current && propertyDropdownListRef.current) {
      selectedPropertyItemRef.current.scrollIntoView({ block: 'center' });
    }
  }, [propertyDropdownOpen]);

  // Auto-switch away from 'all' when navigating away from pages that support it
  useEffect(() => {
    const supportsAll = ['messages', 'knowledge_gaps', 'reservations'];
    const currentPathSupportsAll = supportsAll.some(p => location.pathname.includes(p));

    if (tabActiveHotelId === 'all' && !currentPathSupportsAll && location.pathname !== '/') {
      const firstProperty = properties[0];
      if (firstProperty) {
        setSelectedHotelId(String(firstProperty.id));
      }
    }
  }, [location.pathname, properties, tabActiveHotelId]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await api.get('/user/properties');
        if (res.data?.success) {
          setProperties(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch properties", err);
      }
    };
    fetchProperties();
  }, [api]);

  const isAllProperties = tabActiveHotelId === 'all';
  const currentProperty = properties.find(p => p.id == tabActiveHotelId);
  const currentPropertyName = isAllProperties ? 'All Properties' : (currentProperty ? currentProperty.name : 'All properties');

  // Resolve profile image URL
  let profileImageUrl = user?.profile_picture_url;
  if (profileImageUrl && typeof profileImageUrl === 'string' && profileImageUrl.startsWith('/uploads')) {
    try {
      profileImageUrl = `${new URL(API_BASE).origin}${profileImageUrl}`;
    } catch (e) {
      console.error("Invalid API_BASE or image path", e);
    }
  }

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);
      if (newIsMobile) setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/knowledge_gaps') return 'Dashboard';
    if (path === '/requests') return 'Service Requests';
    if (path === '/messages') return 'Guest Messages';
    if (path === '/reservations') return 'Reservations';
    if (path.startsWith('/knowledge_base')) return 'Knowledge Base';
    if (path.startsWith('/settings')) return 'Settings';
    return 'Dashboard';
  };

  return (
    <div
      className={`min-h-screen relative overflow-hidden transition-colors duration-300 ${isLight ? "bg-[#F5F5F7] text-neutral-900" : "bg-[#05070A] text-neutral-100"
        }`}
    >
      {/* Liquid background blobs (dark mode only) */}
      {!isLight && (
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-gradient-to-br from-white/6 to-white/0 blur-3xl opacity-70" />
          <div className="absolute -bottom-40 -right-10 w-[420px] h-[420px] rounded-full bg-gradient-to-tr from-white/4 to-white/0 blur-3xl opacity-60" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-gradient-to-b from-white/5 to-transparent blur-3xl opacity-40" />
        </div>
      )}

      {/* Full-width layout wrapper */}
      <div className="w-full h-[100dvh] flex overflow-hidden">
        {/* Sidebar - Fixed full height */}
        <aside
          className={`fixed top-0 left-0 h-[100dvh] z-50 flex flex-col w-60 backdrop-blur-2xl border-r shadow-[0_28px_80px_rgba(0,0,0,0.18)] transition-transform duration-300 ${isMobile ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'
            } ${isLight ? "bg-white border-neutral-200" : "bg-white/5 border-white/5"}`}
        >
          <div className="px-6 pt-5 pb-2 flex items-start justify-between">
            <div>
              <div
                className={`text-[10px] uppercase tracking-[0.18em] mb-0.5 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}
              >
                KYPSER Suite
              </div>
              <div className="text-lg font-semibold tracking-tight">AI Concierge</div>
            </div>
            {/* Close button for mobile */}
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                className={`p-1.5 rounded-lg ${isLight ? "hover:bg-neutral-100 text-neutral-500" : "hover:bg-white/10 text-neutral-400"}`}
              >
                <X size={18} />
              </button>
            )}
          </div>

          <nav className={`mt-2 flex-1 space-y-1 px-3 pb-8 overflow-y-auto ${isLight ? 'custom-scroll-light' : 'custom-scroll'}`}>
            {!isSettingsMode ? (
              <>

                <NavItem
                  to="/knowledge_gaps"
                  icon="sparkles"
                  isLight={isLight}
                  properties={properties}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  Dashboard
                </NavItem>
                {/* Service Requests hidden per user request
                <NavItem
                  to="/requests"
                  icon="chat"
                  isLight={isLight}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  Service Requests
                </NavItem>
                */}
                <NavItem
                  to="/messages"
                  icon="guests"
                  isLight={isLight}
                  properties={properties}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  Guest Messages
                </NavItem>
                <NavItem
                  to="/reservations"
                  icon="calendar"
                  isLight={isLight}
                  properties={properties}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  Reservations
                </NavItem>
                <NavItem
                  to="/knowledge_base"
                  icon="docs"
                  isLight={isLight}
                  properties={properties}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  Knowledge Base
                </NavItem>
              </>
            ) : (
              <>
                <p className={`px-4 text-[9px] uppercase tracking-[0.2em] font-bold mb-1 ${isLight ? "text-neutral-400" : "opacity-40"}`}>Preferences</p>
                <NavItem
                  to="/settings/account"
                  icon="user"
                  isLight={isLight}
                  properties={properties}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  Account
                </NavItem>
                <NavItem
                  to="/settings/properties"
                  icon="building"
                  isLight={isLight}
                  properties={properties}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  Manage Properties
                </NavItem>
              </>
            )}
          </nav>

          {/* Settings Toggle & Logout */}
          <div className={`px-3 pb-4 space-y-2 border-t pt-4 ${isLight ? "border-neutral-100" : "border-white/5"}`}>
            <button
              onClick={() => {
                if (isSettingsMode) {
                  navigate('/knowledge_gaps');
                } else {
                  navigate('/settings/account');
                }
                if (isMobile) setSidebarOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-2xl text-sm transition-all duration-300 ${isSettingsMode
                ? (isLight ? "bg-neutral-900 text-white shadow-md" : "bg-white text-black shadow-lg shadow-white/5")
                : (isLight ? "text-neutral-700 hover:bg-neutral-200" : "text-neutral-300 hover:bg-white/10")
                }`}
            >
              <div className="flex items-center gap-3">
                <Settings size={18} />
                <span className="font-semibold">{isSettingsMode ? "Back" : "Settings"}</span>
              </div>

            </button>
            <button
              onClick={handleLogout}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm transition ${isLight
                ? "text-red-600 hover:bg-red-50"
                : "text-red-400 hover:bg-red-500/10"
                }`}
            >
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full border ${isLight ? "border-red-300" : "border-red-400/30"
                  }`}
              >
                <LogOut size={12} />
              </span>
              <span>Log Out</span>
            </button>
          </div>

          <div
            className={`px-6 py-4 border-t text-xs ${isLight ? "border-neutral-200 text-neutral-500" : "border-white/5 text-neutral-500"
              }`}
          >
            Concierge AI <span aria-hidden="true">&middot;</span> v1.0
          </div>
        </aside>

        {/* Mobile sidebar overlay */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className={`flex-1 flex flex-col gap-6 md:ml-60 px-4 sm:px-6 lg:px-10 pt-6 pb-12 overflow-y-auto ${isLight ? 'custom-scroll-light' : 'custom-scroll'}`}>
          <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10">
            {/* Top mask to hide content behind the gap */}
            <div className={`h-6 -mt-6 ${isLight ? 'bg-[#F5F5F7]' : 'bg-[#05070A]'}`} />
            
            <header
              className={`rounded-3xl backdrop-blur-2xl px-4 sm:px-6 py-4 flex items-center justify-between gap-4 border ${isLight
                ? "bg-white/80 border-neutral-200 shadow-[0_18px_40px_rgba(15,23,42,0.12)]"
                : "bg-white/5 border-white/5 shadow-[0_24px_70px_rgba(0,0,0,0.75)]"
                }`}
            >
            {/* Mobile menu button */}
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg relative z-10 ${isLight ? "hover:bg-neutral-100" : "hover:bg-white/10"}`}
              >
                <Menu size={20} />
              </button>
            )}

            <div className="flex-1 min-w-0 ml-2 sm:ml-0">
              <div
                className={`hidden sm:block text-xs uppercase tracking-[0.22em] mb-1 ${isLight ? "text-neutral-500" : "text-neutral-400"
                  }`}
              >
                Hotel AI Overview
              </div>
              <h1 className="text-base sm:text-2xl md:text-3xl font-semibold tracking-tight truncate">{getPageTitle()}</h1>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-4">
              {/* Date Range Dropdown (Only for Knowledge Gaps) */}
              {location.pathname.includes('knowledge_gaps') && (
                <>
                  {selectedDateRange === 'Custom Range' && (
                    <div className="mr-2">
                      <DateRangeFilter
                        from={customFrom}
                        to={customTo}
                        onApply={({ from: f, to: t }) => {
                          setCustomFrom(f);
                          setCustomTo(t);
                          setSelectedDateRange(`${f} → ${t}`);
                          setShowCustomDatePicker(false);
                        }}
                        onClear={() => {
                          setCustomFrom(null);
                          setCustomTo(null);
                          setSelectedDateRange('Last 7 days');
                          setShowCustomDatePicker(false);
                        }}
                        maxPastMonths={4}
                        disableFuture
                        align="right"
                        isLight={isLight}
                        open={showCustomDatePicker}
                        onOpenChange={setShowCustomDatePicker}
                      />
                    </div>
                  )}
                  <div className="relative" ref={dateDropdownRef}>
                  {/* Visual trigger */}
                  <button
                    onClick={() => {
                      // always toggle the dropdown menu; selecting 'Custom Range' inside will open the picker
                      setDateDropdownOpen(!dateDropdownOpen);
                    }}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2.5 rounded-full text-[11px] sm:text-sm font-medium border transition-colors cursor-pointer ${isLight
                      ? 'bg-white border-neutral-200 text-neutral-700 shadow-sm hover:bg-neutral-50'
                      : 'bg-white/5 border-neutral-800 text-neutral-200 hover:bg-neutral-800'
                      }`}>
                    {isMobile ? (
                      <Calendar size={16} />
                    ) : (
                      <span className="truncate">{selectedDateRange}</span>
                    )}
                    <ChevronDown size={isMobile ? 12 : 14} className={`opacity-60 flex-shrink-0 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`} />
                  </button>

                  {/* Custom Dropdown Menu */}
                  {dateDropdownOpen && (
                    <>
                      <div className={`absolute right-0 mt-2 w-48 rounded-2xl shadow-2xl border py-2 z-50 backdrop-blur-xl ${isLight ? "bg-white/90 border-neutral-200" : "bg-[#0A0A0A]/90 border-white/10"}`}>
                        {["Last 24 hours", "Last 7 days", "Last 30 days", "Custom Range"].map((option) => (
                          <button
                            key={option}
                            onClick={() => {
                              if (option === 'Custom Range') {
                                setSelectedDateRange(option);
                                setDateDropdownOpen(false);
                                setShowCustomDatePicker(true);
                              } else {
                                setSelectedDateRange(option);
                                setDateDropdownOpen(false);
                                setShowCustomDatePicker(false);
                                setCustomFrom(null);
                                setCustomTo(null);
                              }
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${isLight
                              ? "text-neutral-700 hover:bg-neutral-100"
                              : "text-neutral-200 hover:bg-white/10"
                              } ${selectedDateRange === option ? (isLight ? "bg-neutral-200 font-medium" : "bg-white/10 font-medium") : ""}`}
                          >
                            <span className="truncate">{option}</span>
                            {selectedDateRange === option && (
                              <div className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  
                </div>
                </>
              )}

              {/* Property Dropdown */}
              <div className="relative" ref={propertyDropdownRef}>
                <button
                  onClick={() => setPropertyDropdownOpen(!propertyDropdownOpen)}
                  className={`flex items-center gap-1.5 sm:gap-3 px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full text-[11px] sm:text-sm font-medium border transition-colors whitespace-nowrap max-w-[150px] sm:max-w-none ${isLight
                    ? 'bg-white border-neutral-200 text-neutral-700 shadow-sm hover:bg-neutral-50'
                    : 'bg-white/5 border-neutral-800 text-neutral-200 hover:bg-neutral-800'
                    }`}
                >
                  {isMobile ? (
                    <Building2 size={16} />
                  ) : (
                    <span className="truncate block max-w-[100px] sm:max-w-xs">{currentPropertyName}</span>
                  )}
                  <ChevronDown size={isMobile ? 12 : 14} className={`opacity-60 flex-shrink-0 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`} />
                </button>

                {propertyDropdownOpen && (
                  <>
                    <div ref={propertyDropdownListRef} className={`absolute right-0 mt-2 w-72 rounded-2xl shadow-2xl border py-2 z-50 backdrop-blur-xl max-h-60 overflow-y-auto minimal-scrollbar ${isLight ? "bg-white/90 border-neutral-200" : "bg-[#0A0A0A]/90 border-white/10"}`}>
                      <div className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>
                        Switch Property
                      </div>
                      {/* All Properties option – visible on Messages and Dashboard pages */}
                      {(location.pathname.includes('messages') || location.pathname.includes('knowledge_gaps') || location.pathname.includes('reservations')) && (
                        <button
                          ref={isAllProperties ? selectedPropertyItemRef : null}
                          onClick={() => {
                            setSelectedHotelId('all');
                            setPropertyDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${isLight
                            ? 'text-neutral-700 hover:bg-neutral-100'
                            : 'text-neutral-200 hover:bg-white/10'
                          } ${isAllProperties ? (isLight ? 'bg-neutral-200 font-medium' : 'bg-white/10 font-medium') : ''}`}
                        >
                          <span className="truncate">All Properties</span>
                          {isAllProperties && (
                            <div className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                          )}
                        </button>
                      )}
                      {properties.length > 0 ? (
                        properties.map((prop) => {
                          const isSelected = !isAllProperties && prop.id == tabActiveHotelId;
                          return (
                            <button
                              key={prop.id}
                              ref={isSelected ? selectedPropertyItemRef : null}
                              onClick={async () => {
                                try {
                                  await switchProperty(String(prop.id));
                                  setPropertyDropdownOpen(false);
                                } catch (error) {
                                  console.error("Failed to switch property", error);
                                }
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${isLight
                                ? "text-neutral-700 hover:bg-neutral-100"
                                : "text-neutral-200 hover:bg-white/10"
                                } ${isSelected ? (isLight ? "bg-neutral-200 font-medium" : "bg-white/10 font-medium") : ""}`}
                            >
                              <span className="truncate">{prop.name}</span>
                              {isSelected && (
                                <div className="h-1.5 w-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <div className={`px-4 py-2 text-sm ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>No other properties</div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Theme toggle - circular button with border */}
              <div className={`h-8 w-[1px] hidden sm:block ${isLight ? "bg-neutral-300" : "bg-white/10"}`}></div>
              <button
                type="button"
                onClick={() => setTheme(isLight ? "dark" : "light")}
                className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center transition-colors border ${isLight
                  ? "bg-neutral-100 text-neutral-600 border-neutral-300 hover:bg-neutral-200"
                  : "bg-transparent text-neutral-400 border-neutral-600 hover:bg-white/5 hover:text-neutral-300"
                  }`}
                aria-label={isLight ? "Switch to dark mode" : "Switch to light mode"}
              >
                {isLight ? (
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="4" strokeWidth={2} />
                    <path strokeLinecap="round" strokeWidth={2} d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                  </svg>
                ) : (
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Avatar - Solid purple - hidden on very small screens */}
              <button
                className="hidden sm:flex h-10 w-10 rounded-full items-center justify-center text-sm font-semibold text-white overflow-hidden shadow-inner border border-white/10"
                style={{ backgroundColor: 'transparent' }}
              >
                <img
                  src={profileImageUrl || profileDefaultImage}
                  alt="Profile"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.target.src = profileDefaultImage;
                  }}
                />
              </button>
            </div>
          </header>
          </div>

          {/* Page content */}
          <ErrorBoundary>
            <div className="flex-1 min-h-0 relative">
              <Outlet context={{ theme, selectedDateRange, properties, customFrom, customTo, setSelectedDateRange, setCustomFrom, setCustomTo, setShowCustomDatePicker }} />
            </div>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

/* ---------- NavItem Component ---------- */
function NavItem({ children, to, icon, isLight, onClick, properties }) {
  const { tabActiveHotelId, setSelectedHotelId, switchProperty } = useAuth();
  
  const handleClick = async () => {
    // If navigating to a page that doesn't support 'all', switch to the first property
    const supportsAll = ['messages', 'knowledge_gaps', 'reservations'];
    const targetSupportsAll = supportsAll.some(p => to.includes(p));
    
    if (tabActiveHotelId === 'all' && !targetSupportsAll) {
      const firstProperty = properties[0];
      if (firstProperty) {
        await switchProperty(String(firstProperty.id));
      }
    }
    if (onClick) onClick();
  };

  return (
    <NavLink
      to={to}
      onClick={handleClick}
      className={({ isActive }) => {
        const activeClasses = isLight
          ? "bg-neutral-900 text-white shadow-[0_12px_30px_rgba(15,23,42,0.55)]"
          : "bg-white/15 text-neutral-50 shadow-[0_12px_30px_rgba(0,0,0,0.8)]";

        const idleClasses = isLight
          ? "text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900"
          : "text-neutral-400 hover:bg-white/6 hover:text-neutral-100";

        return `w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm transition ${isActive ? activeClasses : idleClasses
          }`;
      }}
    >
      <span
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px] ${isLight ? "border-neutral-300" : "border-white/10"
          }`}
      >
        {icon === "home" && <Home size={14} />}
        {icon === "sparkles" && <LayoutDashboard size={14} />}
        {icon === "chat" && <MessageSquare size={14} />}
        {icon === "guests" && <Users size={14} />}
        {icon === "calendar" && <Calendar size={14} />}
        {icon === "docs" && <FileText size={14} />}
        {icon === "settings" && <Settings size={14} />}
        {icon === "user" && <User size={14} />}
        {icon === "building" && <Building2 size={14} />}
      </span>
      <span>{children}</span>
    </NavLink>
  );
}

export default Dashboard;
